import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/b2b/pricing").then((r) => setPlans(r.data.plans)).catch(() => {});
  useEffect(() => { load(); }, []);

  const choose = async (plan) => {
    if (!user) { navigate("/login"); return; }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/b2b/subscribe", { plan_id: plan.id, origin_url: window.location.origin });
      if (data.contact) { toast.success(data.message); navigate("/for-business"); }
      else if (data.checkout_url) { window.location.href = data.checkout_url; }
    } catch { toast.error("Could not start checkout"); } finally { setBusy(null); }
  };

  const saveEdit = async () => {
    try { await api.post("/b2b/pricing", edit); toast.success("Plan updated"); setEdit(null); load(); }
    catch { toast.error("Save failed"); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.18em] text-teal-600 font-semibold">T5 for Business</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-blue-950 mt-2">Plans that grow with your care organization</h1>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">From a single caregiver to multi-state enterprise. Start on a SaaS plan or talk to us for custom deployments.</p>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mt-12 items-stretch">
          {plans.map((p) => (
            <div key={p.id} className={`bg-white rounded-2xl border p-5 flex flex-col ${p.tier === 2 ? "border-teal-500 ring-2 ring-teal-500/20 shadow-lg" : "border-slate-200 shadow-sm"}`} data-testid={`plan-${p.key}`}>
              {p.tier === 2 && <span className="self-start text-[10px] uppercase font-bold bg-teal-500 text-white rounded-full px-2 py-0.5 mb-2">Most popular</span>}
              <h3 className="font-display text-lg font-bold text-blue-950">{p.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5 min-h-[32px]">{p.tagline}</p>
              <div className="mt-3">
                {p.cta_type === "contact"
                  ? <p className="font-display text-2xl font-bold text-blue-950">Contact sales</p>
                  : <p className="font-display text-3xl font-bold text-blue-950">${p.price}<span className="text-sm font-normal text-slate-400">/{p.interval}</span></p>}
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {p.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" /> {f}</li>)}
              </ul>
              <Button onClick={() => choose(p)} disabled={busy === p.id} data-testid={`plan-cta-${p.key}`}
                className={`mt-5 w-full gap-1.5 ${p.tier === 2 ? "bg-teal-600 hover:bg-teal-700" : "bg-blue-900 hover:bg-blue-950"}`}>
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : p.cta_type === "contact" ? <Sparkles className="w-4 h-4" /> : null}
                {p.cta_type === "contact" ? "Request a demo" : "Subscribe"}
              </Button>
              {isAdmin && <button onClick={() => setEdit({ ...p })} data-testid={`plan-edit-${p.key}`} className="mt-2 text-xs text-slate-400 hover:text-blue-900 flex items-center gap-1 justify-center"><Pencil className="w-3 h-3" /> Edit</button>}
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Payments in test mode (Stripe sandbox). Custom & enterprise contracts are quoted by our team.</p>

        {edit && (
          <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setEdit(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="plan-edit-modal">
              <h3 className="font-display font-bold text-blue-950 mb-4">Edit {edit.name}</h3>
              <div className="space-y-3">
                <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Name" data-testid="edit-name" />
                <Input value={edit.tagline} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} placeholder="Tagline" />
                <Input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: Number(e.target.value) })} placeholder="Price" data-testid="edit-price" />
                <select value={edit.interval} onChange={(e) => setEdit({ ...edit, interval: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm">
                  {["month", "year", "contact", "custom"].map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
                <select value={edit.cta_type} onChange={(e) => setEdit({ ...edit, cta_type: e.target.value })} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm">
                  {["checkout", "contact"].map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
                <Input value={(edit.features || []).join("; ")} onChange={(e) => setEdit({ ...edit, features: e.target.value.split(";").map((s) => s.trim()).filter(Boolean) })} placeholder="Features (; separated)" />
              </div>
              <div className="flex gap-2 mt-5"><Button onClick={saveEdit} data-testid="edit-save" className="bg-teal-600 hover:bg-teal-700 flex-1">Save</Button><Button variant="outline" onClick={() => setEdit(null)} className="flex-1">Cancel</Button></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
